// -----------------------------------------------------------------------------
// ui/preview/stage.ts — the preview canvas + transport bar.
//
// Hosts the compositor's canvas (the engine draws into it on its own clock) and a
// transport bar beneath it: jump-to-start, play/pause, and a time / duration
// readout. The UI only sends play/pause/seek to the player and reflects its
// events — it never draws frames itself. (CLAUDE.md §4 principle 5.)
// -----------------------------------------------------------------------------
import { projectDuration } from '../../core/model';
import { formatTimecode } from '../../shared/time';
import type { EditorContext } from '../context';
import { el } from '../dom';

export function createStage(ctx: EditorContext, previewCanvas: HTMLCanvasElement): HTMLElement {
  const { player, store } = ctx;

  const stage = el('div', { class: 'zf-stage' }, previewCanvas);

  const toStart = el(
    'button',
    { class: 'zf-btn zf-btn-ghost', title: 'Jump to start', onclick: () => player.seek(0) },
    '⏮',
  );
  const playBtn = el(
    'button',
    { class: 'zf-btn zf-btn-primary zf-play', title: 'Play / pause (Space)', onclick: () => player.toggle() },
    '▶',
  );
  const timeEl = el('span', { class: 'zf-time' }, '0:00.00 / 0:00.00');

  const transport = el(
    'div',
    { class: 'zf-transport' },
    toStart,
    playBtn,
    timeEl,
    el('span', { class: 'zf-transport-spacer' }),
    el('span', { class: 'zf-time' }, `${store.state.project.width}×${store.state.project.height} · ${store.state.project.fps}fps`),
  );

  const updateTime = (): void => {
    timeEl.textContent = `${formatTimecode(player.getTime())} / ${formatTimecode(projectDuration(store.state.project))}`;
  };
  player.events.on('time', updateTime);
  player.events.on('playing', (playing) => {
    playBtn.textContent = playing ? '❚❚' : '▶';
  });
  store.subscribe(updateTime);
  updateTime();

  return el('div', { class: 'zf-stage-wrap' }, stage, transport);
}
